import usePermissions from 'src/shared/hooks/usePermissions';
import { Access, Resources } from 'src/shared/types';

type OrganisationAdminRights = {
    /** May create and rename organisations and projects. */
    canManage: boolean;
    /** May delete them outright — asked separately, being the one act that cannot be taken back. */
    canDelete: boolean;
};

/**
 * What the current user may do to organisations and projects through the admin screens.
 *
 * Asks about the resource rather than naming a role, so access can be widened from data.
 * A Cognito group already reaches this check: the backend converts groups into synthetic
 * permission rows (`pkg/util/permissions.go` `getGroupPermission`, appended in
 * `getUnCachedUserPermissions`), and `SUPER_ADMIN` receives a wildcard customer with
 * wildcard resources — so a super-admin passes both checks without any grant of its own.
 *
 * Deletion is a separate question rather than another entry in the same `to` list, because
 * `hasPermission` treats that list as "any of these": adding `delete` alongside `create` would
 * hand the delete affordance to anyone who could merely create. The two travel together today,
 * and this keeps them able to part company without the split becoming a hole.
 *
 * The ops-admin role therefore needs no frontend change: granting it this resource, either
 * as a `getGroupPermission` case or a `permissions` row, is enough.
 */
const useCanAdministerOrganisations = (): OrganisationAdminRights => {
    const { hasPermission } = usePermissions();
    const can = (to: Access[]) => hasPermission({ to, resource: Resources.ADMIN_ORG_MANAGEMENT });

    return {
        canManage: can([Access.CREATE, Access.UPDATE]),
        canDelete: can([Access.DELETE]),
    };
};

export default useCanAdministerOrganisations;
